import React, { useEffect, useCallback } from 'react';
import { useComplaintsStore } from '../../store/complaintsStore';
import { type Complaint } from '../../services/complaintsService';
import { SelectWithLabel } from '../ui/universal-select';
import { ManagementPagination } from '../ui/ManagementPagination';
import { useBrandingColors } from '../../hooks/useBrandingColors';

interface ComplaintsListProps {
  onViewComplaint: (complaint: Complaint) => void;
  onReplyToComplaint: (complaint: Complaint) => void;
}

export function ComplaintsList({ onViewComplaint, onReplyToComplaint }: ComplaintsListProps) {
  const colors = useBrandingColors();
  const {
    complaints,
    total,
    page,
    size,
    totalPages,
    filters,
    isLoading,
    error,
    setFilters,
    loadComplaints,
    loadComplaintById
  } = useComplaintsStore();


  // Загружаем жалобы при монтировании компонента
  useEffect(() => {
    loadComplaints();
  }, []);

  // Обработчики фильтров
  const handleStatusFilter = useCallback((status: string) => {
    setFilters({ status, page: 0 });
    loadComplaints();
  }, [setFilters, loadComplaints]);


  const handlePageChange = useCallback((newPage: number) => {
    setFilters({ page: newPage });
    loadComplaints();
  }, [setFilters, loadComplaints]);

  // Форматирование даты
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Получение цвета статуса
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'text-orange-600 bg-orange-100';
      case 'in_progress':
        return 'text-blue-600 bg-blue-100';
      case 'closed':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  // Получение текста статуса
  const getStatusText = (status: string) => {
    switch (status) {
      case 'new':
        return 'Новая';
      case 'in_progress':
        return 'В работе';
      case 'closed':
        return 'Закрыта';
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full min-h-0 flex flex-col gap-4">
      {/* Заголовок */}
      <div className="box-border content-stretch flex flex-row items-center justify-between p-0 relative shrink-0 w-full">
        <div className="css-sqkidj font-['Inter_Display:Medium',_sans-serif] leading-[0] not-italic relative shrink-0 text-[#000000] text-[24px] text-left text-nowrap">
          <p className="block leading-[30px] whitespace-pre">Жалобы</p>
        </div>
      </div>
      
      {/* Основной контент */}
      <div className="flex flex-col gap-2 flex-1">
        {/* Фильтры */}
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex items-end gap-3">
            <div style={{ width: '192px' }}>
              <SelectWithLabel
                label="Статус"
                value={filters.status || 'all'}
                onChange={(value) => handleStatusFilter(String(value))}
                options={[
                  { value: 'all', label: 'Все' },
                  { value: 'new', label: 'Новые' },
                  { value: 'in_progress', label: 'В работе' },
                  { value: 'closed', label: 'Закрытые' }
                ]}
                className="w-full"
                openUpward={false}
              />
            </div>
          </div>
          
          
          <button
            onClick={() => loadComplaints()}
            className="bg-white border px-[12px] rounded-2xl text-[16px] h-[42px] transition-colors flex items-center justify-center"
            style={{ borderColor: colors.primary, color: colors.primary }}
          >
            ↻ Обновить
          </button>
        </div>

        {/* Таблица жалоб */}
        <div className="bg-white rounded-[21px] p-2">
          <div className="flex flex-col gap-[3.75px]">
            {/* Заголовки таблицы */}
            <div className="bg-[#f6f8fa] rounded-[10px] flex">
              <div className="px-3.5 py-[10.5px] w-20">
                <span className="font-medium text-[14px] text-[#525866]">ID</span>
              </div>
              <div className="px-3.5 py-[10.5px] w-32">
                <span className="font-medium text-[14px] text-[#525866]">Дата</span>
              </div>
              <div className="px-3.5 py-[10.5px] w-48">
                <span className="font-medium text-[14px] text-[#525866]">Автор</span>
              </div>
              <div className="px-3.5 py-[10.5px] flex-1">
                <span className="font-medium text-[14px] text-[#525866]">Описание</span>
              </div>
              <div className="px-3.5 py-[10.5px] w-32">
                <span className="font-medium text-[14px] text-[#525866]">Статус</span>
              </div>
              <div className="px-3.5 py-[10.5px] w-24">
                <span className="font-medium text-[14px] text-[#525866]">Действия</span>
              </div>
            </div>

            {/* Строки данных */}
            <div className="flex flex-col gap-[1.2px]">
              {complaints.map((complaint) => (
                <div key={complaint.id} className="flex flex-col gap-[1.75px]">
                  <div className="bg-white rounded-xl min-h-16 flex">
                    {/* ID */}
                    <div className="px-3.5 py-[10.5px] w-20 flex items-center">
                      <span className="font-medium text-[14px] text-[#0a0d14]">#{complaint.id}</span>
                    </div>

                    {/* Дата */}
                    <div className="px-3.5 py-[10.5px] w-32 flex items-center">
                      <span className="font-normal text-[14px] text-[#0a0d14]">{formatDate(complaint.createdAt)}</span>
                    </div>

                    {/* Автор */}
                    <div className="px-3.5 py-[10.5px] w-48 flex items-center">
                      <span className="font-normal text-[14px] text-[#0a0d14] truncate">{complaint.authorEmail}</span>
                    </div>

                    {/* Описание */}
                    <div className="px-3.5 py-[10.5px] flex-1 flex items-center min-w-0">
                      <span className="font-normal text-[14px] text-[#0a0d14] truncate w-full" title={complaint.description}>
                        {complaint.description ? complaint.description.substring(0, 100) + (complaint.description.length > 100 ? '...' : '') : 'Нет описания'}
                      </span>
                    </div>

                    {/* Статус */}
                    <div className="px-3.5 py-[10.5px] w-32 flex items-center">
                      <span className={`inline-flex px-2 py-1 text-[14px] font-semibold rounded-full ${getStatusColor(complaint.status)}`}>
                        {getStatusText(complaint.status)}
                      </span>
                    </div>

                    {/* Действия */}
                    <div className="px-3.5 py-[10.5px] w-24 flex items-center gap-2">
                      <button
                        onClick={async () => {
                          await loadComplaintById(complaint.id);
                          onViewComplaint(complaint);
                        }}
                        className="text-[#e16349] hover:text-[#d14a31] p-1 transition-colors"
                        title="Посмотреть"
                      >
                        👁️
                      </button>
                      <button
                        onClick={() => onReplyToComplaint(complaint)}
                        className="text-[#e16349] hover:text-[#d14a31] p-1 transition-colors"
                        title="Ответить"
                      >
                        💬
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Пагинация внизу общего контейнера */}
      {totalPages > 1 && (
        <ManagementPagination
          currentPage={page + 1}
          totalPages={totalPages}
          totalItems={total}
          itemsPerPage={size}
          onPageChange={(newPage) => handlePageChange(newPage - 1)}
        />
      )}
    </div>
  );
}
